import { useState } from "react";
import { cn } from "@/lib/utils";
import { Gem, Check, ArrowRight } from "lucide-react";

interface PricingTier {
  name: string;
  price: { monthly: number; annual: number };
  description: string;
  features: string[];
  cta: string;
  popular?: boolean;
}

const tiers: PricingTier[] = [
  {
    name: "Free",
    price: { monthly: 0, annual: 0 },
    description: "For hobby projects and testing",
    features: [
      "10,000 requests/month",
      "Current weather & 3-day forecast",
      "Standard response times",
      "Community support",
    ],
    cta: "Get started free",
  },
  {
    name: "Developer",
    price: { monthly: 29, annual: 24 },
    description: "For side projects in production",
    features: [
      "100,000 requests/month",
      "7-day forecast + historical",
      "Priority edge routing",
      "Email support",
      "Webhook notifications",
    ],
    cta: "Start free trial",
  },
  {
    name: "Pro",
    price: { monthly: 99, annual: 79 },
    description: "For production apps with users",
    features: [
      "500,000 requests/month",
      "14-day forecast + 30-day historical",
      "Air quality & UV index",
      "Webhook alerts",
      "Priority support",
      "Team members (up to 5)",
    ],
    cta: "Start free trial",
    popular: true,
  },
  {
    name: "Scale",
    price: { monthly: 249, annual: 199 },
    description: "For high-traffic applications",
    features: [
      "2,000,000 requests/month",
      "Full data suite",
      "Dedicated edge pool",
      "99.99% SLA guarantee",
      "Slack support channel",
      "Unlimited team members",
      "Custom data retention",
    ],
    cta: "Contact sales",
  },
];

interface PricingProps {
  className?: string;
}

export function Pricing({ className }: PricingProps) {
  const [isAnnual, setIsAnnual] = useState(false);

  return (
    <section
      id="pricing"
      className={cn(
        "py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-white to-sky-50",
        className
      )}
    >
      <div className="max-w-7xl mx-auto">
        {/* Section header */}
        <div className="text-center mb-12">
          <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-sky-100 text-sky-700 text-sm font-medium rounded-full mb-4">
            <Gem className="w-3.5 h-3.5" /> Pricing
          </span>
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-800 mb-4">
            Simple, transparent pricing
          </h2>
          <p className="text-lg text-slate-500 max-w-2xl mx-auto mb-8">
            Start free, scale as you grow. No credit card required.
          </p>

          {/* Billing toggle */}
          <div className="flex items-center justify-center gap-3">
            <span
              className={cn(
                "text-sm font-medium transition-colors",
                !isAnnual ? "text-slate-800" : "text-slate-500"
              )}
            >
              Monthly
            </span>
            <button
              onClick={() => setIsAnnual(!isAnnual)}
              className={cn(
                "relative w-14 h-7 rounded-full transition-colors",
                isAnnual ? "bg-sky-500" : "bg-slate-200"
              )}
              aria-label="Toggle annual billing"
            >
              <span
                className={cn(
                  "absolute top-1 w-5 h-5 bg-white rounded-full shadow transition-transform",
                  isAnnual ? "translate-x-8" : "translate-x-1"
                )}
              />
            </button>
            <span
              className={cn(
                "text-sm font-medium transition-colors",
                isAnnual ? "text-slate-800" : "text-slate-500"
              )}
            >
              Annual{" "}
              <span className="text-emerald-600 text-xs font-semibold ml-1">
                Save 20%
              </span>
            </span>
          </div>
        </div>

        {/* Pricing cards */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {tiers.map((tier) => (
            <div
              key={tier.name}
              className={cn(
                "relative rounded-2xl p-6 transition-all duration-300",
                tier.popular
                  ? "bg-gradient-to-b from-sky-600 to-sky-700 text-white ring-4 ring-sky-200 scale-[1.02] shadow-xl"
                  : "bg-white border border-slate-200 hover:border-sky-200 hover:shadow-lg"
              )}
            >
              {/* Popular badge */}
              {tier.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <span className="px-3 py-1 bg-white/20 text-white text-xs font-semibold rounded-full backdrop-blur-sm">
                    Most Popular
                  </span>
                </div>
              )}

              {/* Tier name */}
              <h3
                className={cn(
                  "text-lg font-semibold mb-1",
                  tier.popular ? "text-white" : "text-slate-800"
                )}
              >
                {tier.name}
              </h3>

              {/* Description */}
              <p
                className={cn(
                  "text-sm mb-4",
                  tier.popular ? "text-sky-200" : "text-slate-500"
                )}
              >
                {tier.description}
              </p>

              {/* Price */}
              <div className="mb-6">
                <span
                  className={cn(
                    "text-4xl font-bold",
                    tier.popular ? "text-white" : "text-slate-800"
                  )}
                >
                  ${isAnnual ? tier.price.annual : tier.price.monthly}
                </span>
                <span
                  className={cn(
                    "text-sm",
                    tier.popular ? "text-sky-200" : "text-slate-500"
                  )}
                >
                  /month
                </span>
              </div>

              {/* Features list */}
              <ul className="space-y-3 mb-6">
                {tier.features.map((feature) => (
                  <li
                    key={feature}
                    className={cn(
                      "flex items-start gap-2 text-sm",
                      tier.popular ? "text-sky-100" : "text-slate-600"
                    )}
                  >
                    <span
                      className={cn(
                        "flex-shrink-0 mt-0.5",
                        tier.popular ? "text-sky-300" : "text-emerald-500"
                      )}
                    >
                      <Check className="w-4 h-4" />
                    </span>
                    {feature}
                  </li>
                ))}
              </ul>

              {/* CTA button */}
              <a
                href={tier.name === "Scale" ? "/contact" : "/signup"}
                className={cn(
                  "block w-full py-2.5 px-4 text-center text-sm font-medium rounded-lg transition-colors",
                  tier.popular
                    ? "bg-white text-sky-700 hover:bg-sky-50"
                    : "bg-slate-900 text-white hover:bg-slate-800"
                )}
              >
                {tier.cta}
              </a>
            </div>
          ))}
        </div>

        {/* Enterprise callout */}
        <div className="mt-12 text-center">
          <p className="text-slate-500 mb-2">
            Need more? We offer custom enterprise plans.
          </p>
          <a
            href="/contact"
            className="inline-flex items-center gap-1 text-sky-600 font-semibold hover:text-sky-700 transition-colors"
          >
            Contact sales for a custom quote <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </div>
    </section>
  );
}

export default Pricing;
